import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';

import type { Consent } from '@/stores/user.store';

import VerifyEmailBannerDataProvider from '@/components/modals/account/VerifyEmailBannerDataProvider.vue';
import { initTrackers } from '@/main.common';
import { CollectionType } from '@/enums/collection-type';

import { logTalentAd } from '@/helpers/ad/talent-ad';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { isSameWeek } from '@/helpers/date-helper';
import { updateUserProfile } from '@/helpers/id-helper';
import { initSentry } from '@/helpers/sentry-helper';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { isMobile } from '@/helpers/ui-helper';
import { isArbitrating } from '@/helpers/composables/useArbitrage';
import TitleRemovalBanner from '@/components/banners/TitleRemovalBanner.vue';
import { lowPriorityCallback } from '@/helpers/request-idle-helper';
import { useLanguageStore, useRoutingStore, useUserStore } from '@/helpers/composables/useStores';
import { getVm } from '@/helpers/vm-helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'LowPriorityComponents',
  setup(__props) {

const ConsentBanner = () => import(/* webpackChunkName: "consent-banner" */ '@/components/consent/ConsentBanner.vue');
const ListsForSignedInUsersOnboarding = () =>
	import(/* webpackChunkName: "consent-banner" */ '@/components/modals/ListsForSignedInUsersOnboarding.vue');
const ConsentOverlay = () =>
	import(/* webpackChunkName: "consent-overlay" */ '@/components/consent/ConsentOverlay.vue');
const FilterMenu = () => import(/* webpackChunkName: "filter-menu" */ '@/components/filter-bar/FilterMenu.vue');
const MobileBanner = () => import(/* webpackChunkName: "mobile-banner" */ '@/components/MobileBanner.vue');

const vm = getVm();

const { country, language, webLocale } = useLanguageStore();
const { lastActiveCollectionType, activeCollectionType } = useRoutingStore();
const { saveSettings, updateUserVisitStatus, isLoggedIn, jwId, lastTimeMobileAppBannerWasClosedTS } = useUserStore();

const shouldRenderMenu = ref(false);

const consentComponent = computed(() => {
	if (!global.VUE_APP_TESTS_E2E && hasUCConsentBanner(webLocale.value)) {
		return ConsentOverlay;
	}

	return ConsentBanner;
});
const showMobileAppBanner = computed(() => {
	return (
		!isLoggedIn.value &&
		!isSameWeek(new Date(lastTimeMobileAppBannerWasClosedTS.value), new Date()) &&
		!isArbitrating.value
	);
});

watch(
	() => activeCollectionType.value,
	newActiveCollectionType => {
		if (
			!shouldRenderMenu.value &&
			!!newActiveCollectionType &&
			[
				CollectionType.NEW,
				CollectionType.POPULAR,
				CollectionType.SEARCH,
				CollectionType.MY_LISTS,
				CollectionType.TV_SHOW_TRACKING,
				CollectionType.PUBLIC_LISTS,
				CollectionType.TITLELIST,
			].includes(newActiveCollectionType)
		) {
			shouldRenderMenu.value = true;
		}
	},
	{ immediate: true }
);

onMounted(() => {
	if (process.env.NODE_ENV !== 'development') {
		logTalentAd();
	}

	lowPriorityCallback(() => initTrackers(vm.$store));
	lowPriorityCallback(() => initSentry());
	updateUserVisitStatus();

	if (isMobile()) {
		// @ts-ignore
		import('long-press-event/dist/long-press-event.min.js');
	}

	// NOTE This is only for testing (otherwise we have no way to force old consents)
	(window as any).saveSettings = async (consents: Record<Consent, boolean>) => {
		await saveSettings({ jw_consents: consents });
	};

	if (country.value) {
		try {
			updateUserProfile(jwId.value, country.value, language.value);
		} catch (error: any) {
			TrackingHelper.trackEvent('app_error', {
				action: 'update_user_profile_failure',
				label: error,
			});
		}
	}
});

return { __sfc: true,ConsentBanner, ListsForSignedInUsersOnboarding, ConsentOverlay, FilterMenu, MobileBanner, vm, country, language, webLocale, lastActiveCollectionType, activeCollectionType, saveSettings, updateUserVisitStatus, isLoggedIn, jwId, lastTimeMobileAppBannerWasClosedTS, shouldRenderMenu, consentComponent, showMobileAppBanner, VerifyEmailBannerDataProvider, TitleRemovalBanner }
}

})